// Widget block (widget {kind, size, label, ...}): a Material 3 card that shows live data. Kinds:
//   clock {tz?, h24} · weather {city, locate} (locate = the visitor's own place: browser location, else IP, else the city) · counts {what: photos|videos|articles} · visits {period: day|month|year}
//   chart {range: 7|30|90|365, look: area|bars} (page views per day) · server {metric: all|cpu|memory|storage} (live meters, refreshed every 10 s)
//   pictures {set?, every, fit} (a slideshow of one photo set, or of all published photos) · like {id} (heart + counter, one per visitor)
//   pageviews {page?} · pagelikes {page?} · share (copies the link): per-page counters kept by address on the server (page = another page's address, default the one shown)
// size s | m | l scales the card's content; the space it takes comes from the block width on the page or its span in a grid.
// Data comes from /api/widgets/* (apps/portal/widgets.js). Registered as ExtraBlocks.widget, so pages and grids render it like any block.
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect, useLayoutEffect, useRef } = React;

  const SCALE = {
    // height: every compact widget of a size is exactly this tall, so a row of them lines up; weather, the slideshow and the meter list may grow.
    // Small is the clock / weather look and the default; Full is for widgets that run across the whole row.
    s: { height: 168, big: 'displaySmall', icon: 36, pad: 2.5 },
    m: { height: 240, big: 'displaySmall', icon: 40, pad: 2.5 },
    l: { height: 320, big: 'displayMedium', icon: 48, pad: 3 },
    f: { height: 400, big: 'displayLarge', icon: 56, pad: 3 }
  };

  // The session token (if signed in) goes with likes and votes, so one account counts once whichever device it votes from
  const authHeaders = () => { try { const t = localStorage.getItem('wb_token'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch (e) { return {}; } };
  const goSignIn = () => { window.location.hash = `#/signin?next=${encodeURIComponent(`/${window.location.hash}`)}`; };
  window.WidgetAuth = { headers: authHeaders, signIn: goSignIn };
  // One request for the counters, shared by every widget on the page for a minute
  let statsAt = 0, statsP = null;
  const loadStats = () => {
    if (!statsP || Date.now() - statsAt > 60e3) {
      statsAt = Date.now();
      statsP = fetch('/api/widgets/stats').then(r => (r.ok ? r.json() : Promise.reject(new Error('stats')))).catch(e => { statsP = null; throw e; });
    }
    return statsP;
  };
  // Loads once per key; null while loading, false when it failed
  function useFetch(load, key) {
    const [data, setData] = useState(null);
    useEffect(() => {
      let live = true;
      setData(null);
      load().then(d => { if (live) setData(d); }).catch(() => { if (live) setData(false); });
      return () => { live = false; };
    }, [key]);
    return data;
  }

  // Chosen background (b.surface / b.bg, as on grids). The text and accent roles the card reads are pointed at the matching ink.
  function surfaceSx(b) {
    const { GRID_SURFACE, customSurface } = window.Components.Surfaces || {};
    if (!b.surface || !GRID_SURFACE) return null;
    let base;
    if (b.surface === 'custom') base = b.bg ? customSurface(b.bg) : null;
    else if (GRID_SURFACE[b.surface]) {
      const [bg, ink] = GRID_SURFACE[b.surface];
      base = { backgroundColor: bg, color: ink, '--m3-on-surface': ink, '--m3-on-surface-variant': `color-mix(in srgb, ${ink} 80%, transparent)`, '--m3-primary': ink, '--m3-outline': `color-mix(in srgb, ${ink} 50%, transparent)`, '--m3-outline-variant': `color-mix(in srgb, ${ink} 22%, transparent)` };
    }
    if (!base) return null;
    return { ...base, '--w-accent': base['--m3-on-surface'] || 'currentColor', '--m3-tertiary': base['--m3-on-surface'] || 'currentColor', '--m3-surface-container-highest': `color-mix(in srgb, ${base['--m3-on-surface'] || 'currentColor'} 16%, transparent)` };
  }

  function Shell({ b, icon, label, action, children, flush, grow, innerRef, hidden, sx, onClick }) {
    const { Card, GoogleIcon } = window.Components;
    const sc = SCALE[b.size] || SCALE.s;
    return (
      <Card variant="filled" {...(onClick ? { clickable: true, onClick } : { role: 'group' })} aria-label={typeof label === 'string' ? label : undefined} sx={{ my: 3, ...(grow ? { minHeight: sc.height } : { height: sc.height }), overflow: 'hidden', position: 'relative', containerType: 'inline-size', '--w-accent': 'var(--m3-primary)', ...(hidden && { visibility: 'hidden' }),
        // Inside a grid with a background the card is a tint of that grid's colours: its ink, accent and tracks follow the grid
        '.blk-surface &': { backgroundColor: 'color-mix(in srgb, currentColor 10%, transparent)', color: 'inherit', '--w-accent': 'currentColor', '--m3-on-surface': 'currentColor', '--m3-on-surface-variant': 'color-mix(in srgb, currentColor 80%, transparent)', '--m3-outline-variant': 'color-mix(in srgb, currentColor 22%, transparent)', '--m3-surface-container-highest': 'color-mix(in srgb, currentColor 16%, transparent)' },
        ...surfaceSx(b), ...sx }}>
        <Box ref={innerRef} sx={{ height: '100%', minHeight: 'inherit', display: 'flex', flexDirection: 'column', p: flush ? 0 : sc.pad, ...(!grow && { boxSizing: 'border-box' }) }}>
          {!flush && (label || icon) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--m3-on-surface-variant)', mb: 1, ...(action && { justifyContent: 'space-between' }) }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {icon && <GoogleIcon name={icon} size={20} />}
                {label && <Typography variant="body2" component="span" sx={{ color: 'inherit', ...window.Components.m3Typescale.labelLarge }}>{label}</Typography>}
              </Box>
              {action}
            </Box>
          )}
          {children}
        </Box>
      </Card>
    );
  }
  // The big figure of a widget; its font is Admin › Site settings › Widget numbers (--wb-font-numbers)
  const Big = ({ b, children }) => <Typography component="p" sx={{ m: 0, color: 'var(--m3-on-surface)', lineHeight: 1.1, ...window.Components.m3Typescale[(SCALE[b.size] || SCALE.s).big], fontFamily: "var(--wb-font-numbers, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)", fontSynthesis: 'none' }}>{children}</Typography>;
  const Sub = ({ children }) => <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)', mt: 0.5 }}>{children}</Typography>;
  const Unavailable = () => <Sub>Not available right now.</Sub>;

  function Clock({ b }) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
    const tz = b.tz || undefined;
    // Formatters made once per zone / format (toLocale*String builds a new Intl formatter on every call, every second)
    const fmt = React.useMemo(() => {
      try {
        return [new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: b.h24 === false, timeZone: tz }), new Intl.DateTimeFormat([], { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz })];
      } catch (e) { return null; } // an unknown zone falls back to the visitor's own
    }, [tz, b.h24]);
    const time = fmt ? fmt[0].format(now) : now.toLocaleTimeString();
    const date = fmt ? fmt[1].format(now) : '';
    return (
      <Shell b={b} icon="schedule" label={b.label || (b.tz ? b.tz.split('/').pop().replace(/_/g, ' ') : 'Local time')}>
        <Box sx={{ mt: 'auto' }}><Big b={b}>{time}</Big><Sub>{date}</Sub></Box>
      </Shell>
    );
  }

  // ---- animated weather icons --------------------------------------------------------------------------------------------------
  // The same glyphs as before (outlines in weatherIconData.js), drawn as SVG so that only the moving parts move: rain and snow fall, the lightning
  // bolt flashes, fog lines slide, the sun's rays fade in and out without moving. The cloud and everything else stays exactly as designed. Still for reduced motion.
  const WEATHER_CSS = `
    @keyframes wbw-drop { 0% { transform: translate(-42px, -90px); opacity: 0.2; } 18% { opacity: 1; } 72% { opacity: 1; } 100% { transform: translate(42px, 90px); opacity: 0.2; } }
    @keyframes wbw-drizzle { 0%, 100% { opacity: 0.25; } 50% { opacity: 1; } }
    @keyframes wbw-flake { 0% { transform: translate(0, -70px); opacity: 0.2; } 20% { opacity: 1; } 55% { transform: translate(28px, 18px); } 80% { opacity: 1; } 100% { transform: translate(-10px, 64px); opacity: 0.2; } }
    @keyframes wbw-bolt { 0%, 52%, 100% { opacity: 1; } 56% { opacity: 0.35; } 61% { opacity: 1; } 66% { opacity: 0.4; } 74% { opacity: 1; } }
    @keyframes wbw-fog { 0%, 100% { transform: translateX(-48px); opacity: 0.6; } 50% { transform: translateX(48px); opacity: 1; } }
    @keyframes wbw-glow { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
    .wbw-drop { animation: wbw-drop 1.5s linear infinite; }
    .wbw-drizzle { animation: wbw-drizzle 2.4s ease-in-out infinite; }
    .wbw-flake { animation: wbw-flake 3.2s ease-in-out infinite; }
    .wbw-bolt { animation: wbw-bolt 3.4s linear infinite; }
    .wbw-fog { animation: wbw-fog 5s ease-in-out infinite; }
    .wbw-ray { animation: wbw-glow 4s ease-in-out infinite; } /* the rays stay in place and only fade softly, never fully gone, like the drizzle; each ray gets its own delay and length (WeatherIcon) so they never pulse together */
    @media (prefers-reduced-motion: reduce) { .wbw-drop, .wbw-drizzle, .wbw-flake, .wbw-bolt, .wbw-fog, .wbw-ray { animation: none !important; } }`;
  function WeatherIcon({ code, size = 24, delay = 0, style }) {
    useEffect(() => {
      if (!document.getElementById('wb-weather-css')) { const st = document.createElement('style'); st.id = 'wb-weather-css'; st.textContent = WEATHER_CSS; document.head.appendChild(st); }
    }, []);
    const g = (window.WeatherIconData || {})[WMO(code)[1]];
    if (!g) return null;
    return (
      <svg viewBox="0 -960 960 960" width={size} height={size} aria-hidden="true" style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', overflow: 'visible', fill: 'currentColor', color: 'var(--w-accent, var(--m3-primary))', ...style }}>
        {g.base.map((d, i) => <path key={`b${i}`} d={d} />)}
        {g.parts.map((p, i) => <path key={i} d={p.d} className={`wbw-${p.k}`} style={p.k === 'ray' ? { animationDelay: `-${((i * 1.7 + 0.3) % 4).toFixed(2)}s`, animationDuration: `${(2.4 + ((i * 37 + 5) % 11) / 4).toFixed(2)}s` } : { animationDelay: `${(delay + i * (p.k === 'bolt' ? 0 : 0.37)).toFixed(2)}s` }} />)}
      </svg>
    );
  }

  // WMO weather codes -> label + Material Symbol
  const WMO = c => (c === 0 ? ['Clear', 'sunny'] : c <= 2 ? ['Partly cloudy', 'partly_cloudy_day'] : c === 3 ? ['Overcast', 'cloud'] : c <= 48 ? ['Fog', 'foggy']
    : c <= 57 ? ['Drizzle', 'rainy_light'] : c <= 67 ? ['Rain', 'rainy'] : c <= 77 ? ['Snow', 'weather_snowy'] : c <= 82 ? ['Showers', 'rainy'] : c <= 86 ? ['Snow showers', 'weather_snowy'] : ['Thunderstorm', 'thunderstorm']);
  function Weather({ b }) {
    const { GoogleIcon, IconButton, Tooltip, Button } = window.Components;
    // Own loader (not useFetch) so a failure can say what to fix. With b.locate the visitor's place comes from the browser only when
    // they already allowed it or press "Use my location" (never a permission prompt on page load; rounded coordinates go to our server
    // only), else from their IP (Cloudflare), then the typed city.
    const [w, setW] = useState(null);
    const [err, setErr] = useState('');
    const [near, setNear] = useState(false); // shown for the visitor's own location
    const [qs, setQs] = useState(''); // the query that found this place, reused by the full forecast
    const [more, setMore] = useState(false);
    const [hideCard, setHideCard] = useState(false); // the card is hidden while its dialog is open, so the dialog reads as the card grown
    const cardEl = useRef(null); // the card's inner box: its parent is the card
    const [asked, setAsked] = useState(0); // "Use my location" presses: each one may show the browser's permission prompt
    const [precise, setPrecise] = useState(false); // this place came from the browser's location
    const canLocate = !!(b.locate && navigator.geolocation);
    useEffect(() => {
      let live = true;
      if (!asked) { setW(null); setErr(''); setNear(false); setQs(''); } // a press keeps the current card until the new place is in
      const ask = qs => fetch(`/api/widgets/weather?${qs}`).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) { const e = new Error(d.error || 'weather unavailable'); e.status = r.status; throw e; } return d; });
      const here = () => new Promise((ok, no) => (navigator.geolocation ? navigator.geolocation.getCurrentPosition(p => ok(p.coords), no, { timeout: 8000, maximumAge: 600000 }) : no(new Error('no geolocation'))));
      const granted = () => (navigator.permissions && navigator.permissions.query
        ? navigator.permissions.query({ name: 'geolocation' }).then(s => s.state === 'granted', () => false) : Promise.resolve(false));
      const run = async () => {
        if (b.locate && (asked || await granted())) {
          try { const c = await here(); const q = `lat=${c.latitude.toFixed(2)}&lon=${c.longitude.toFixed(2)}`; return [await ask(q), true, q, true]; } catch (e) { /* denied or unavailable: try the IP next */ }
        }
        if (b.locate) {
          try { return [await ask('ip=1'), true, 'ip=1']; } catch (e) { /* unknown place: the typed city is next */ }
        }
        if (!b.city) throw new Error(b.locate ? 'Could not find your location. Use my location below, or set a city in the widget settings.' : 'Enter a city in the widget settings.');
        try { const q = `city=${encodeURIComponent(b.city)}`; return [await ask(q), false, q]; } catch (e) { throw new Error(e.status === 404 ? `City “${b.city}” not found.` : 'Weather service not reachable right now.'); }
      };
      run().then(([d, mine, q, exact]) => { if (live) { setW(d); setNear(mine); setQs(q); setPrecise(!!exact); setErr(''); } }).catch(e => { if (live) { setW(false); setErr(e.message); } });
      return () => { live = false; };
    }, [b.city, b.locate, asked]);
    const sc = SCALE[b.size] || SCALE.s;
    const [text, icon] = w ? WMO(w.code) : ['', 'cloud'];
    return (
      <Shell b={b} grow innerRef={cardEl} hidden={hideCard} icon="location_on" label={b.label || (w && w.city) || (near ? 'Your location' : b.city) || 'Weather'}
        action={(w && qs) || (canLocate && w && !precise) ? (
          <Box sx={{ display: 'flex', flexShrink: 0, my: -0.5, mr: -0.5 }}>
            {canLocate && w && !precise && (
              // The browser's permission prompt only follows this press, never the page load
              <Tooltip title="Use my location">
                <IconButton size="small" aria-label="Use my location" onClick={() => setAsked(n => n + 1)}><GoogleIcon name="my_location" size={22} /></IconButton>
              </Tooltip>
            )}
            {w && qs && (
              <Tooltip title="Full forecast">
                <IconButton size="small" aria-label="Full forecast" onClick={() => { setHideCard(true); setMore(true); }}><GoogleIcon name="calendar_month" size={22} /></IconButton>
              </Tooltip>
            )}
          </Box>
        ) : null}>
        {w === null && <Sub>Loading…</Sub>}
        {w === false && <Sub>{err}</Sub>}
        {w === false && canLocate && (
          <Box sx={{ mt: 1 }}><Button variant="tonal" size="small" icon={<GoogleIcon name="my_location" size={18} />} onClick={() => setAsked(n => n + 1)}>Use my location</Button></Box>
        )}
        {w && (
          // Minimal in a narrow card (temperature, conditions, high / low). The extra info goes to the right of it once the card is
          // 480px wide, and below it on phones, where cards are stacked full width.
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2, '@container (min-width: 480px)': { flexDirection: 'row', alignItems: 'flex-end', gap: 3 } }}>
            {/* The weather icon leads, centred on the temperature and conditions beside it */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
              <WeatherIcon code={w.code} size={sc.icon * 1.5} />
              <Box sx={{ minWidth: 0 }}><Big b={b}>{w.temp}°</Big><Sub>{text} · {w.low}° / {w.high}°</Sub></Box>
            </Box>
            {b.size && b.size !== 's' && (
              <Box sx={{ display: 'none', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 1.5, '@container (min-width: 480px)': { display: 'grid', flex: '0 0 52%' }, '@media (max-width: 899.95px)': { display: 'grid' }, 'html[data-narrow] &': { display: 'grid' } }}>
                {[['Feels like', `${w.feels}°`], ['Humidity', `${w.humidity}%`], ['Wind', `${w.wind} km/h`], ['Rain chance', `${w.rain}%`], ['UV index', w.uv], ['Sunrise', w.sunrise], ['Sunset', w.sunset]].map(([k, v]) => (
                  <Box key={k}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--m3-on-surface-variant)' }}>{k}</Typography>
                    <Typography variant="body1" sx={{ color: 'var(--m3-on-surface)', fontWeight: 500 }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {w && (b.size === 'l' || b.size === 'f') && (
          // Five-day outlook (large and full): from 440px wide, or on phones
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid var(--m3-outline-variant)', display: 'none', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1, '@container (min-width: 440px)': { display: 'grid' }, '@media (max-width: 899.95px)': { display: 'grid' }, 'html[data-narrow] &': { display: 'grid' } }}>
            {w.days.map((day, k) => (
              <Box key={day.day} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'var(--m3-on-surface-variant)' }}>{k ? new Date(`${day.day}T12:00:00`).toLocaleDateString([], { weekday: 'short' }) : 'Today'}</Typography>
                <WeatherIcon code={day.code} size={28} delay={k * 0.4} style={{ margin: '4px 0' }} />
                <Typography variant="body2" sx={{ color: 'var(--m3-on-surface)' }}>{day.high}° <span style={{ color: 'var(--m3-on-surface-variant)' }}>{day.low}°</span></Typography>
                <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>{day.rain}%</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Typography variant="caption" sx={{ mt: 1, color: 'var(--m3-on-surface-variant)' }}>
          Data from <Box component="a" href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>Open-Meteo</Box>
        </Typography>
        {qs && <Forecast qs={qs} open={more} origin={() => cardEl.current && cardEl.current.parentElement} place={b.label || (w && w.city) || (near ? 'Your location' : b.city)} onClose={() => setMore(false)} onExited={() => setHideCard(false)} />}
      </Shell>
    );
  }

  // Full forecast dialog: now, the next 48 hours (strip + temperature chart) and ten days. Loads when opened, with the same place as the card (qs).
  function Forecast({ qs, place, open, origin, onClose, onExited }) {
    const { Dialog, Button, GoogleIcon } = window.Components;
    const [d, setD] = useState(null);
    useEffect(() => {
      if (!open) return undefined; // loads when first opened (and again if the place changed)
      let live = true;
      fetch(`/api/widgets/weather?${qs}&full=1`).then(r => (r.ok ? r.json() : Promise.reject(new Error('forecast')))).then(x => { if (live) setD(x); }).catch(() => { if (live) setD(false); });
      return () => { live = false; };
    }, [qs, open]);
    const dayName = (iso, k) => (k === 0 ? 'Today' : k === 1 ? 'Tomorrow' : new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' }));
    const stat = (k, v) => (
      <Box key={k}>
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--m3-on-surface-variant)' }}>{k}</Typography>
        <Typography variant="body1" sx={{ color: 'var(--m3-on-surface)', fontWeight: 500 }}>{v}</Typography>
      </Box>
    );
    const lo = d ? Math.min(...d.days10.map(x => x.low)) : 0, hi = d ? Math.max(...d.days10.map(x => x.high)) : 1;
    // Memoized on the data: opening or closing the dialog re-renders Forecast, and redoing 48 hours, a chart and 10 days at that moment stalls the
    // morph animation (its clip-path runs on the main thread)
    const body = React.useMemo(() => (d ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <WeatherIcon code={d.code} size={64} />
              <Box>
                <Typography component="p" sx={{ m: 0, lineHeight: 1.1, color: 'var(--m3-on-surface)', ...window.Components.m3Typescale.displaySmall }}>{d.temp}°</Typography>
                <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)' }}>{WMO(d.code)[0]} · {d.low}° / {d.high}°</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 260, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 1.5 }}>
                {[['Feels like', `${d.feels}°`], ['Humidity', `${d.humidity}%`], ['Wind', `${d.wind} km/h`], ['Rain chance', `${d.rain}%`], ['UV index', d.uv], ['Sunrise', d.sunrise], ['Sunset', d.sunset]].map(([k, v]) => stat(k, v))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>Next 48 hours</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 1, mb: 1 }}>
                {d.hours.map((h, k) => {
                  const hh = h.t.slice(11, 16);
                  return (
                    <Box key={h.t} sx={{ flex: '0 0 auto', width: 64, textAlign: 'center', py: 1, borderRadius: 'var(--m3-shape-medium)', ...(hh === '00:00' && { borderLeft: '1px solid var(--m3-outline-variant)' }) }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'var(--m3-on-surface-variant)' }}>{k === 0 ? 'Now' : hh === '00:00' ? new Date(`${h.t.slice(0, 10)}T12:00:00`).toLocaleDateString([], { weekday: 'short' }) : hh}</Typography>
                      <WeatherIcon code={h.code} size={26} delay={(k % 8) * 0.3} style={{ margin: '3px 0' }} />
                      <Typography variant="body2" sx={{ color: 'var(--m3-on-surface)', fontWeight: 500 }}>{h.temp}°</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>{h.rain}%</Typography>
                    </Box>
                  );
                })}
              </Box>
              <TimeChart points={d.hours.map((h, k) => ({ label: `${k === 0 ? 'Now' : h.t.slice(11, 16)}`, value: h.temp }))} look="area" min={Math.floor(Math.min(...d.hours.map(h => h.temp)) / 5) * 5} max={Math.ceil(Math.max(...d.hours.map(h => h.temp)) / 5) * 5 + 5} height={140} unit="°" xLabels={false} ariaLabel="Temperature over the next 48 hours" />
            </Box>

            <Box>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>10 days</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {d.days10.map((x, k) => (
                  <Box key={x.day} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '210px 36px 1fr 150px' }, alignItems: 'center', gap: { xs: 1, sm: 2 }, py: 1.25, borderTop: k ? '1px solid var(--m3-outline-variant)' : 'none' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'var(--m3-on-surface)', fontWeight: 500 }}>{dayName(x.day, k)}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>{WMO(x.code)[0]} · rain {x.rain}%{x.precip ? ` · ${x.precip} mm` : ''}</Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}><WeatherIcon code={x.code} size={28} delay={k * 0.35} /></Box>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ width: 34, textAlign: 'right', color: 'var(--m3-on-surface-variant)' }}>{x.low}°</Typography>
                      <Box sx={{ flex: 1, position: 'relative', height: 6, borderRadius: 3, backgroundColor: 'var(--m3-surface-container-highest)' }}>
                        <Box sx={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 3, backgroundColor: 'var(--w-accent, var(--m3-primary))', left: `${((x.low - lo) / Math.max(1, hi - lo)) * 100}%`, right: `${100 - ((x.high - lo) / Math.max(1, hi - lo)) * 100}%` }} />
                      </Box>
                      <Typography variant="body2" sx={{ width: 34, color: 'var(--m3-on-surface)' }}>{x.high}°</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ textAlign: 'right', color: 'var(--m3-on-surface-variant)' }}>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' }, color: 'var(--m3-on-surface)', mr: 1 }}>{x.low}° / {x.high}°</Box>
                      wind {x.wind} km/h · UV {x.uv}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: 'var(--m3-on-surface-variant)' }}>
              Data from <Box component="a" href="https://open-meteo.com/" target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>Open-Meteo</Box>
            </Typography>
          </Box>
        
    ) : null), [d]);
    return (
      <Dialog open={open} origin={origin} onExited={onExited} onClose={onClose} maxWidth="md" headline={<Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" component="h2" noWrap sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.2px' }}>{(d && d.city) || place || 'Weather'}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Full forecast</Typography>
      </Box>} actions={<Button variant="text" onClick={onClose}>Close</Button>}>
        {d === null && <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>Loading…</Typography>}
        {d === false && <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>The forecast is not available right now.</Typography>}
        {body}
      </Dialog>
    );
  }

  const COUNTS = { photos: ['photo_library', 'Photos'], videos: ['videocam', 'Videos'], articles: ['article', 'Articles'] };
  function Counts({ b }) {
    const [icon, name] = COUNTS[b.what] || COUNTS.photos;
    const d = useFetch(loadStats, 'stats');
    return (
      <Shell b={b} icon={icon} label={b.label || name}>
        {d === false ? <Unavailable /> : <Box sx={{ mt: 'auto' }}><Big b={b}>{d ? d[b.what] : '–'}</Big><Sub>published in total</Sub></Box>}
      </Shell>
    );
  }

  const PERIOD = { day: 'Visits today', month: 'Visits this month', year: 'Visits this year' };
  function Visits({ b }) {
    const d = useFetch(loadStats, 'stats');
    const n = d && d.visits ? d.visits[b.period || 'day'] : null;
    return (
      <Shell b={b} icon="trending_up" label={b.label || PERIOD[b.period] || PERIOD.day}>
        {d === false ? <Unavailable /> : <Box sx={{ mt: 'auto' }}><Big b={b}>{n == null ? '–' : n.toLocaleString()}</Big><Sub>page views, no admins or bots</Sub></Box>}
      </Shell>
    );
  }

  // Slideshow: dissolves through the photos every `every` seconds; pauses while the tab is hidden.
  // Dissolve, not a fade through the background: the previous picture stays underneath at full strength while the new one fades in on
  // top (with fit 'contain' the previous one fades out too, its letterbox would show around the new one). Only the shown, previous and
  // next pictures are in the page, so a big set does not load all at once; each keeps its element (keyed by photo), so nothing reloads.
  function Pictures({ b }) {
    const photos = useFetch(() => fetch(`/api/widgets/pictures${b.set ? `?set=${b.set}` : ''}`).then(r => (r.ok ? r.json() : Promise.reject(new Error('pictures')))).then(d => d.photos), b.set || 'all');
    const [s, setS] = useState({ i: 0, prev: -1, turn: 0 });
    const n = photos ? photos.length : 0;
    const i = n ? s.i % n : 0;
    useEffect(() => {
      if (n < 2) return undefined;
      const t = setInterval(() => { const a = window.M3Core && window.M3Core.getA11y(); if (!document.hidden && !(a && a.nodeco) && !(window.M3Core && window.M3Core.reducedMotion())) setS(v => ({ i: (v.i + 1) % n, prev: v.i % n, turn: v.turn + 1 })); }, (b.every || 6) * 1000);
      return () => clearInterval(t);
    }, [n, b.every]);
    const contain = b.fit === 'contain';
    // The box is measured before the first paint and the pictures are only put in once it is known: `sizes` is then the width each
    // picture is really drawn at (cropped to fill a tall box it is wider than the box), so the browser fetches a sharp enough copy
    const boxRef = useRef(null);
    const [box, setBox] = useState(null);
    useLayoutEffect(() => {
      const el = boxRef.current;
      if (!el) return undefined;
      const measure = () => setBox(o => (o && o[0] === el.clientWidth && o[1] === el.clientHeight ? o : [el.clientWidth, el.clientHeight]));
      measure();
      if (!('ResizeObserver' in window)) return undefined;
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);
    const sizesFor = p => {
      const ratio = p.width && p.height ? p.width / p.height : 1.5;
      const drawn = contain ? Math.min(box[0], box[1] * ratio) : Math.max(box[0], box[1] * ratio);
      return `${Math.max(1, Math.ceil(drawn))}px`;
    };
    const DISSOLVE = '1s var(--m3-easing-standard) both';
    const layer = k => {
      if (k === i) return { zIndex: 2, opacity: 1, ...(s.turn > 0 && { animation: `wbDissolveIn ${DISSOLVE}` }) };
      if (k === s.prev) return { zIndex: 1, opacity: contain ? 0 : 1, ...(contain && { animation: `wbDissolveOut ${DISSOLVE}` }) };
      if (k === (i + 1) % n) return { zIndex: 0, opacity: 0 }; // loads ahead, unseen
      return null;
    };
    return (
      <Shell b={b} flush grow sx={{ minHeight: (SCALE[b.size] || SCALE.s).height + 60 }}>
        <Box ref={boxRef} sx={{ position: 'absolute', inset: 0, backgroundColor: 'var(--m3-surface-container-highest)' }}>
          {box && (photos || []).map((p, k) => {
            const look = layer(k);
            return look && (
              <Box key={p.id} component="img" {...window.Components.responsiveImg(p.url || p.thumbUrl, sizesFor(p))} alt={k === i ? p.title || '' : ''} aria-hidden={k === i ? undefined : 'true'} loading="lazy"
                sx={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: contain ? 'contain' : 'cover', ...look,
                  '@keyframes wbDissolveIn': { from: { opacity: 0 }, to: { opacity: 1 } }, '@keyframes wbDissolveOut': { from: { opacity: 1 }, to: { opacity: 0 } }
                }} />
            );
          })}
          {photos === false && <Box sx={{ p: 2 }}><Unavailable /></Box>}
          {photos && !n && <Box sx={{ p: 2 }}><Sub>No photos yet.</Sub></Box>}
          {b.label && n > 0 && (
            <Typography variant="body2" sx={{ ...window.Components.m3Typescale.labelLarge, position: 'absolute', left: 12, bottom: 12, zIndex: 3, px: 1.5, py: 0.5, borderRadius: 'var(--m3-shape-small)', backgroundColor: 'color-mix(in srgb, var(--m3-surface-container) 85%, transparent)', color: 'var(--m3-on-surface)' }}>{b.label}</Typography>
          )}
        </Box>
      </Shell>
    );
  }

  // Heart + counter. The server counts one like per visitor (IP hash); the heart toggles it. Optimistic, rolled back on failure.
  function Like({ b }) {
    const { GoogleIcon, IconButton, Tooltip } = window.Components;
    const [s, setS] = useState(null);
    const busy = useRef(false);
    useEffect(() => {
      let live = true;
      if (b.id) fetch(`/api/widgets/like/${encodeURIComponent(b.id)}`, { headers: authHeaders() }).then(r => r.json()).then(d => { if (live) setS(d); }).catch(() => {});
      return () => { live = false; };
    }, [b.id]);
    const toggle = async () => {
      if (busy.current || !s || !b.id) return;
      if (s.needLogin) { goSignIn(); return; }
      busy.current = true;
      const before = s;
      setS({ liked: !s.liked, count: s.count + (s.liked ? -1 : 1) });
      try {
        const r = await fetch(`/api/widgets/like/${encodeURIComponent(b.id)}`, { method: 'POST', headers: authHeaders() });
        if (!r.ok) throw new Error('like');
        setS(await r.json());
      } catch (e) { setS(before); } finally { busy.current = false; }
    };
    const sc = SCALE[b.size] || SCALE.s;
    const liked = !!(s && s.liked);
    const title = liked ? 'Remove your like' : s && s.needLogin ? 'Sign in to like this site' : 'Like this site';
    // The site-wide heart: one number for everyone who likes the site, whichever heart on whichever page they pressed
    return (
      <Shell b={b} icon="favorite" label={b.label || 'Loved by'}>
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={title}>
            <span>
              <IconButton aria-label={title} aria-pressed={liked} disabled={!s || !b.id} onClick={toggle}>
                <GoogleIcon name="favorite" size={sc.icon} filled={liked} style={{ color: liked ? 'var(--w-accent, var(--m3-primary))' : 'var(--m3-on-surface-variant)' }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box aria-live="polite" aria-atomic="true" role="status" sx={{ minWidth: 0 }}>
            <Big b={b}>{s ? s.count.toLocaleString() : '–'}</Big>
            {s && <Sub>{s.count === 1 ? 'person likes this site' : 'people like this site'}{liked ? ', including you' : ''}</Sub>}
          </Box>
        </Box>
      </Shell>
    );
  }


  // ---- charts -------------------------------------------------------------------------------------------------------
  // One-series time chart in the theme's primary colour: 2px line over a light area, or bars with rounded tops. Thin recessive grid,
  // value labels in text colours, a crosshair + tooltip on hover (touch too) and a hidden table for screen readers.
  // points: [{ label, value }]; min / max: fixed ends of the scale (min 0 by default, max else rounded up from the data); xLabels: show the first / middle / last label.
  // compact: a small chart that still has its details (0 and top of the scale, first and last label), for cards with little height.
  // minimal: just the line or bars, edge to edge, with no grid or axis text (small and medium cards); the hover tooltip still shows the values.
  function TimeChart({ points, look = 'area', height = 140, min = 0, max, unit = '', xLabels = true, minimal = false, compact = false, ariaLabel }) {
    const ref = useRef(null);
    const [w, setW] = useState(0);
    const [hover, setHover] = useState(null);
    useEffect(() => {
      if (!ref.current) return undefined;
      const ro = new ResizeObserver(e => setW(Math.floor(e[0].contentRect.width)));
      ro.observe(ref.current);
      return () => ro.disconnect();
    }, []);
    const n = points.length;
    const top = max || niceMax(Math.max(1, ...points.map(p => p.value)));
    const L = minimal ? 2 : compact ? 30 : 34, R = minimal ? 2 : 6, T = minimal ? 6 : 8, B = minimal ? 2 : xLabels ? (compact ? 18 : 20) : 6;
    const iw = Math.max(0, w - L - R), ih = height - T - B;
    const xAt = i => L + (look === 'bars' ? (iw / n) * (i + 0.5) : n > 1 ? (iw * i) / (n - 1) : iw / 2);
    const yAt = v => T + ih - ((Math.min(Math.max(v, min), top) - min) / (top - min)) * ih;
    const ticks = (compact ? [0, 1] : [0, 0.5, 1]).map(f => min + (top - min) * f);
    const fmt = v => `${Number(v).toLocaleString()}${unit}`;
    const barW = Math.max(2, Math.min(24, (iw / Math.max(n, 1)) * 0.6));
    const move = e => {
      if (!n || !iw) return;
      const x = e.clientX - ref.current.getBoundingClientRect().left;
      const i = look === 'bars' ? Math.floor(((x - L) / iw) * n) : Math.round(((x - L) / iw) * (n - 1));
      setHover(Math.max(0, Math.min(n - 1, i)));
    };
    const line = points.map((p, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`).join(' ');
    const h = hover != null ? points[hover] : null;
    return (
      <Box ref={ref} sx={{ position: 'relative', width: '100%', height, touchAction: 'pan-y' }} onPointerMove={move} onPointerDown={move} onPointerLeave={() => setHover(null)}>
        {w > 0 && (
          <svg width={w} height={height} role="img" aria-label={ariaLabel || 'Chart'} style={{ display: 'block' }}>
            {!minimal && ticks.map((t, k) => (
              <g key={k}>
                <line x1={L} x2={w - R} y1={yAt(t)} y2={yAt(t)} stroke="var(--m3-outline-variant)" strokeWidth="1" opacity={k ? 0.6 : 1} />
                <text x={L - 6} y={yAt(t) + 4} textAnchor="end" fontSize="11" fill="var(--m3-on-surface-variant)">{Math.round(t).toLocaleString()}</text>
              </g>
            ))}
            {look === 'bars'
              ? points.map((p, i) => {
                const x = xAt(i) - barW / 2, y = yAt(p.value), hgt = T + ih - y, r = Math.min(4, barW / 2, hgt);
                return hgt > 0 && <path key={i} d={`M${x} ${y + hgt}V${y + r}Q${x} ${y} ${x + r} ${y}H${x + barW - r}Q${x + barW} ${y} ${x + barW} ${y + r}V${y + hgt}Z`} fill="var(--w-accent, var(--m3-primary))" opacity={hover == null || hover === i ? 1 : 0.45} />;
              })
              : n > 0 && (
                <>
                  <path d={`${line}L${xAt(n - 1).toFixed(1)} ${T + ih}L${xAt(0).toFixed(1)} ${T + ih}Z`} fill="color-mix(in srgb, var(--w-accent, var(--m3-primary)) 14%, transparent)" />
                  <path d={line} fill="none" stroke="var(--w-accent, var(--m3-primary))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                </>
              )}
            {!minimal && xLabels && n > 0 && (compact ? [0, n - 1] : [0, Math.floor((n - 1) / 2), n - 1]).filter((v, k, a) => a.indexOf(v) === k).map(i => (
              <text key={i} x={xAt(i)} y={height - 4} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize="11" fill="var(--m3-on-surface-variant)">{points[i].label}</text>
            ))}
            {h && look !== 'bars' && (
              <g>
                <line x1={xAt(hover)} x2={xAt(hover)} y1={T} y2={T + ih} stroke="var(--m3-outline)" strokeWidth="1" />
                <circle cx={xAt(hover)} cy={yAt(h.value)} r="5" fill="var(--w-accent, var(--m3-primary))" stroke="var(--m3-surface-container-highest, var(--m3-surface))" strokeWidth="2" />
              </g>
            )}
          </svg>
        )}
        {h && (
          <Box sx={{ position: 'absolute', top: 0, left: Math.min(Math.max(xAt(hover), 60), Math.max(60, w - 60)), transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 2, px: 1.25, py: 0.5, borderRadius: 'var(--m3-shape-small)', backgroundColor: 'var(--m3-inverse-surface)', color: 'var(--m3-inverse-on-surface)', whiteSpace: 'nowrap', ...window.Components.m3Typescale.labelMedium }}>
            {h.label}: <b>{fmt(h.value)}</b>
          </Box>
        )}
        {/* Screen-reader table; the clipped wrapper is a div because overflow does not apply to a table, whose rows would stretch the scroll area */}
        <Box sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          <table><tbody>{points.map((p, i) => <tr key={i}><th scope="row">{p.label}</th><td>{fmt(p.value)}</td></tr>)}</tbody></table>
        </Box>
      </Box>
    );
  }
  // Round the top of the scale up to 1, 2, 2.5, 5 x 10^n so the three gridlines carry clean numbers
  function niceMax(v) {
    const p = 10 ** Math.floor(Math.log10(v));
    return [1, 2, 2.5, 5, 10].map(m => m * p).find(m => m >= v) || v;
  }
  // Chart heights that fit each card size. Small is compact (counter in the header row, the scale and first / last date kept); the others show the full grid.
  const CHART_H = { s: 88, m: 116, l: 176, f: 256 };

  function Chart({ b }) {
    const days = b.range || 30;
    const d = useFetch(() => fetch(`/api/widgets/visits?days=${days}`).then(r => (r.ok ? r.json() : Promise.reject(new Error('visits')))), days);
    const pts = d ? d.points.map(p => ({ label: new Date(`${p.day}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' }), value: p.views })) : [];
    const total = pts.reduce((a, p) => a + p.value, 0);
    const small = !b.size || b.size === 's';
    const label = `${total.toLocaleString()} views`;
    return (
      <Shell b={b} icon="monitoring" label={b.label || `Visits, last ${days === 365 ? 'year' : `${days} days`}`}
        action={small && d ? <Typography variant="body2" component="span" sx={{ flexShrink: 0, color: 'var(--m3-on-surface)', fontWeight: 600 }}>{label}</Typography> : null}>
        {d === false && <Unavailable />}
        {d === null && <Sub>Loading…</Sub>}
        {d && (
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!small && <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}><Big b={{ size: 's' }}>{total.toLocaleString()}</Big><Sub>page views</Sub></Box>}
            <TimeChart points={pts} look={b.look} height={CHART_H[b.size] || CHART_H.s} compact={small} ariaLabel={`Page views per day, last ${days} days, ${total} in total`} />
          </Box>
        )}
      </Shell>
    );
  }

  // ---- server meters ------------------------------------------------------------------------------------------------
  const bytes = n => (n >= 1e12 ? `${(n / 1e12).toFixed(1)} TB` : n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.round(n / 1e6)} MB`);
  const METERS = { cpu: ['CPU', 'memory'], memory: ['Memory', 'developer_board'], storage: ['Storage', 'hard_drive'] };
  // Linear meter; the value is always written out, the colour only adds "getting full" (tertiary from 75%, error from 90%)
  function Meter({ name, pct, detail }) {
    const color = pct >= 90 ? 'var(--m3-error)' : pct >= 75 ? 'var(--m3-tertiary)' : 'var(--w-accent, var(--m3-primary))'; // fuller than 75% / 90% turns to a warning colour
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
          <Typography variant="body2" sx={{ ...window.Components.m3Typescale.labelLarge, color: 'var(--m3-on-surface)' }}>{name}</Typography>
          <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)' }}>{detail ? `${detail} · ` : ''}<b style={{ color: 'var(--m3-on-surface)' }}>{pct}%</b></Typography>
        </Box>
        <Box role="meter" aria-label={name} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} sx={{ height: 8, borderRadius: 4, backgroundColor: 'var(--m3-surface-container-highest)', overflow: 'hidden' }}>
          <Box sx={{ width: `${Math.max(2, pct)}%`, height: '100%', borderRadius: 4, backgroundColor: color, transition: 'width var(--m3-duration-medium, 300ms) var(--m3-easing-standard)' }} />
        </Box>
      </Box>
    );
  }
  function Server({ b }) {
    const [d, setD] = useState(null);
    useEffect(() => {
      let live = true;
      const load = () => { if (!document.hidden) fetch('/api/widgets/server').then(r => (r.ok ? r.json() : Promise.reject(new Error('server')))).then(x => { if (live) setD(x); }).catch(() => { if (live) setD(v => v || false); }); };
      load();
      const t = setInterval(load, 10e3);
      return () => { live = false; clearInterval(t); };
    }, []);
    const metric = b.metric || 'all';
    const [name, icon] = METERS[metric] || ['Server', 'dns'];
    const rows = d ? {
      cpu: { name: 'CPU', pct: d.cpu, detail: `${d.cores} cores · load ${d.load}` },
      memory: { name: 'Memory', pct: d.mem.pct, detail: `${bytes(d.mem.used)} of ${bytes(d.mem.total)}` },
      storage: { name: 'Storage', pct: d.disk.pct, detail: `${bytes(d.disk.total - d.disk.used)} free of ${bytes(d.disk.total)}` }
    } : null;
    const hist = d && (metric === 'cpu' || metric === 'memory') ? d.history.map((h, i, a) => ({ label: i === a.length - 1 ? 'now' : `${Math.round(((a.length - 1 - i) * 10) / 60 * 10) / 10} min ago`, value: metric === 'cpu' ? h.cpu : h.mem })) : null;
    return (
      <Shell b={b} grow icon={icon} label={b.label || (metric === 'all' ? 'Server' : name)}>
        {d === false && <Unavailable />}
        {d === null && <Sub>Loading…</Sub>}
        {d && metric === 'all' && <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>{['cpu', 'memory', 'storage'].map(k => <Meter key={k} {...rows[k]} />)}</Box>}
        {d && metric === 'storage' && <Box sx={{ mt: 'auto' }}><Big b={b}>{rows.storage.pct}%</Big><Sub>{rows.storage.detail}</Sub><Box sx={{ mt: 1.5 }}><Meter name="Used" pct={rows.storage.pct} /></Box></Box>}
        {d && hist && (b.size === 's' || !b.size) && <Box sx={{ mt: 'auto' }}><Big b={b}>{rows[metric].pct}%</Big><Sub>{rows[metric].detail}</Sub></Box>}
        {d && hist && b.size && b.size !== 's' && (
          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box><Big b={b}>{rows[metric].pct}%</Big><Sub>{rows[metric].detail}</Sub></Box>
            <TimeChart points={hist} look="area" max={100} unit="%" xLabels={false} minimal={b.size === 's' || b.size === 'm' || !b.size} height={b.size === 'f' ? 250 : b.size === 'l' ? 170 : 90} ariaLabel={`${name} use over the last 15 minutes, now ${rows[metric].pct}%`} />
          </Box>
        )}
      </Shell>
    );
  }

  // ---- per-page counters and share ------------------------------------------------------------------------------------
  // The address of the page being viewed, as /api/track reports it (the part after #); follows in-app navigation. b.page = another page's address.
  const currentPath = () => '/' + (window.location.hash || '').replace(/^#\/?/, '').split('?')[0];
  function usePagePath(b) {
    const [path, setPath] = useState(currentPath);
    useEffect(() => {
      const on = () => setPath(currentPath());
      window.addEventListener('hashchange', on);
      return () => window.removeEventListener('hashchange', on);
    }, []);
    return b.page ? '/' + String(b.page).replace(/^[#/]+/, '') : path;
  }
  // { views, likes, liked } of a page. The counters live on the server by address, so they exist whether or not any widget shows them.
  function usePageStats(path) {
    const [s, setS] = useState(null);
    useEffect(() => {
      let live = true;
      setS(null);
      fetch(`/api/widgets/page?path=${encodeURIComponent(path)}`, { headers: authHeaders() }).then(r => (r.ok ? r.json() : Promise.reject(new Error('page')))).then(d => { if (live) setS(d); }).catch(() => { if (live) setS(false); });
      return () => { live = false; };
    }, [path]);
    return [s, setS];
  }

  function PageViews({ b }) {
    const path = usePagePath(b);
    const [s] = usePageStats(path);
    return (
      <Shell b={b} icon="visibility" label={b.label || 'Page views'}>
        {s === false ? <Unavailable /> : <Box sx={{ mt: 'auto' }}><Big b={b}>{s ? s.views.toLocaleString() : '–'}</Big><Sub>views of this page, no admins or bots</Sub></Box>}
      </Shell>
    );
  }

  function PageLikes({ b }) {
    const { GoogleIcon, IconButton, Tooltip } = window.Components;
    const path = usePagePath(b);
    const [s, setS] = usePageStats(path);
    const busy = useRef(false);
    const toggle = async () => {
      if (busy.current || !s) return;
      busy.current = true;
      const before = s;
      setS({ ...s, liked: !s.liked, likes: s.likes + (s.liked ? -1 : 1) });
      try {
        const r = await fetch('/api/widgets/page-like', { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ path }) });
        if (!r.ok) throw new Error('like');
        setS(await r.json());
      } catch (e) { setS(before); } finally { busy.current = false; }
    };
    const sc = SCALE[b.size] || SCALE.s;
    const liked = !!(s && s.liked);
    return (
      <Shell b={b} icon="thumb_up" label={b.label || 'Page likes'}>
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={liked ? 'Remove your like' : 'Like this page'}>
            <span>
              <IconButton aria-label={liked ? 'Remove your like' : 'Like this page'} aria-pressed={liked} disabled={!s} onClick={toggle}>
                <GoogleIcon name="thumb_up" size={sc.icon} filled={liked} style={{ color: liked ? 'var(--w-accent, var(--m3-primary))' : 'var(--m3-on-surface-variant)' }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box aria-live="polite" aria-atomic="true" role="status"><Big b={b}>{s ? s.likes : '–'}</Big></Box>
        </Box>
      </Shell>
    );
  }

  // Copies the address of the page being viewed (section and study included: it is the same #/... address that a refresh reopens)
  function Share({ b }) {
    const { GoogleIcon, Button } = window.Components;
    const [done, setDone] = useState(false);
    const copy = async () => {
      const url = window.location.href;
      let ok = false;
      try { await navigator.clipboard.writeText(url); ok = true; } catch (e) {
        try { // no clipboard permission: select a temporary field
          const t = document.createElement('textarea'); t.value = url; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.opacity = '0';
          document.body.appendChild(t); t.select(); ok = document.execCommand('copy'); t.remove();
        } catch (e2) { /* nothing to do */ }
      }
      if (!ok && navigator.share) { try { await navigator.share({ url }); return; } catch (e) { return; } }
      if (window.notify) window.notify(ok ? 'Link copied' : 'Could not copy the link');
      if (ok) { setDone(true); setTimeout(() => setDone(false), 2000); }
    };
    return (
      <Shell b={b} icon="share" label={b.label || 'Share'}>
        <Box sx={{ mt: 'auto' }}>
          <Button variant="tonal" icon={<GoogleIcon name={done ? 'check' : 'link'} size={20} />} onClick={copy}>{done ? 'Link copied' : 'Copy link'}</Button>
        </Box>
      </Shell>
    );
  }

  // Link: a compact card that leads to a page, section, case study or photo set (b.to, as on cards and buttons): a square picture, a
  // title and a description, in the widget sizes (small: text only; medium / large / full: with the picture, growing with the size). For a case study or photo set they come
  // from it (window.LinkedItems, as the card block's "fill"); what is typed on the widget (h, x, u) wins. The whole picture always shows,
  // fitted inside its square (never cropped), on a tinted tile.
  const LINK_SIZE = { s: { title: 'titleMedium', lines: 3 }, m: { title: 'titleLarge', lines: 3 }, l: { title: 'headlineSmall', lines: 4 }, f: { title: 'headlineMedium', lines: 5 } };
  // Clamps a text to the whole lines that fit the room its flex parent leaves it, ending in "…" (re-measured on resize, up to max lines)
  function useFitLines(max, text) {
    const ref = React.useRef(null);
    React.useLayoutEffect(() => {
      const el = ref.current;
      if (!el || !el.parentElement) return undefined;
      // the room = the column's height minus its other children and the gaps, so it does not depend on how many lines show now
      const fit = () => {
        const wrap = el.parentElement, col = el.closest('[data-fit-root]') || wrap.parentElement;
        const cs = getComputedStyle(col), gap = parseFloat(cs.rowGap) || 0, kids = [...col.children];
        const others = kids.filter(k => k !== wrap).reduce((sum, k) => sum + k.getBoundingClientRect().height, 0);
        const room = col.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0) - others - gap * (kids.length - 1);
        const lh = parseFloat(getComputedStyle(el).lineHeight) || 20;
        el.style.webkitLineClamp = String(Math.max(1, Math.min(max, Math.floor((room + 1) / lh))));
      };
      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(el.closest('[data-fit-root]') || el.parentElement);
      return () => ro.disconnect();
    }, [max, text]); // again once the linked text has loaded
    return ref;
  }

  function LinkWidget({ b }) {
    const { GoogleIcon, LabelPills } = window.Components;
    const to = b.to && (b.to.page || b.to.href) ? b.to : null;
    const item = window.LinkedItems ? window.LinkedItems.useLinked(to, true) : null;
    const ls = LINK_SIZE[b.size] || LINK_SIZE.s;
    const title = b.h || (item && item.title) || '';
    const text = b.x || (item && item.text) || '';
    const pic = b.u || (item && item.img) || null;
    const pill = (b.size === 'l' || b.size === 'f') && item && item.pill;
    const clamp = n => ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' });
    const textRef = useFitLines(ls.lines, text);
    // the picture is square: as tall as the card's inside, but never wider than 42% of the card (narrow grid cells)
    const sc = SCALE[b.size] || SCALE.s;
    const side = `min(${sc.height - sc.pad * 16}px, 42cqw)`;
    return (
      <Shell b={b} flush onClick={to ? () => window.SiteLinks.go(to) : undefined}>
        <Box sx={{ height: '100%', display: 'flex', gap: { xs: 2, sm: b.size === 's' ? 2 : 3 }, p: (SCALE[b.size] || SCALE.s).pad, boxSizing: 'border-box', alignItems: 'stretch' }}>
          {b.size !== 's' && <Box sx={{ width: side, height: side, alignSelf: 'center', flex: '0 0 auto', '@container (max-width: 399px)': { display: 'none' } /* a narrow card keeps its room for the text */, borderRadius: 'var(--m3-shape-large, 16px)', overflow: 'hidden', bgcolor: 'var(--m3-secondary-container)', color: 'var(--m3-on-secondary-container)', display: 'grid', placeItems: 'center' }}>{/* a tinted square, so a transparent picture (a round illustration) still reads as a square image */}
            {pic
              ? <Box component="img" src={/^\/uploads\/portfolio\/[\w.-]+\.(?!gif$)\w+$/i.test(pic) ? `${pic}?w=480` : pic} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              : <GoogleIcon name={to && to.slug ? 'article' : to && to.set ? 'photo_library' : 'link'} size={(SCALE[b.size] || SCALE.s).icon} style={{ color: 'inherit' }} />}
          </Box>}
          <Box data-fit-root sx={{ minWidth: 0, minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75, overflow: 'hidden' }}>
            {pill && <Box sx={{ flexShrink: 0 }}><LabelPills items={[pill]} /></Box>}
            {title && <Typography component="h3" sx={{ m: 0, color: 'var(--m3-on-surface)', ...window.Components.m3Typescale[ls.title], fontFamily: 'var(--wb-font-heading)', flexShrink: 0, overflowWrap: 'anywhere', ...clamp(2) }}>{title}</Typography>}
            {text && (
              <Box sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'hidden' }}>
                <Typography ref={textRef} variant="body2" sx={{ m: 0, color: 'var(--m3-on-surface-variant)', overflowWrap: 'anywhere', ...clamp(ls.lines) }}>{text}</Typography>
              </Box>
            )}
            {!title && !text && <Typography variant="body2" sx={{ color: 'var(--m3-on-surface-variant)' }}>{to ? 'Loading…' : 'Choose where this card leads.'}</Typography>}
            {to && <GoogleIcon name="arrow_forward" size={20} style={{ color: 'var(--m3-primary)', marginTop: 4, flexShrink: 0 }} />}
          </Box>
        </Box>
      </Shell>
    );
  }

  const KINDS = { clock: Clock, weather: Weather, counts: Counts, visits: Visits, pictures: Pictures, like: Like, chart: Chart, server: Server, pageviews: PageViews, pagelikes: PageLikes, share: Share, link: LinkWidget, poll: props => <window.Components.PollWidget {...props} /> };
  function Widget({ b }) {
    const View = KINDS[b.kind] || Clock;
    return <View b={b} />;
  }

  window.Components = window.Components || {};
  window.Components.ExtraBlocks = window.Components.ExtraBlocks || {};
  window.Components.ExtraBlocks.widget = Widget;
  // The widget look (card shell, big number, supporting line, meter, chart) for other screens, e.g. the Admin Console's hardware cards
  // Other screens' cards grow with their content (only the widget blocks themselves use the fixed heights)
  const GrowShell = props => <Shell grow {...props} />;
  window.Components.WeatherIcon = WeatherIcon; // animated weather glyph for a WMO weather code
  window.Components.WidgetParts = { Shell: GrowShell, Big, Sub, Meter, TimeChart };
})();
